import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { toastHelper } from '../util/toastHelper.js'

// Base URL and API version for timeline event-related endpoints
const REQUEST_URL = `${process.env.REACT_APP_REQUEST_URL}/api`
const API_VERSION = 'v1'

// Async thunk to fetch a single kazoo
export const checkKazooAuth = createAsyncThunk(
	'kazoo/checkKazooAuth',
	async (kazooId) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/kazoo/check-kazoo-auth/${kazooId}`,
				{
					method: 'GET',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const checkKazooAuthVendor = createAsyncThunk(
	'kazoo/checkKazooAuthVendor',
	async (kazooId) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/kazoo/check-kazoo-auth-vendor/${kazooId}`,
				{
					method: 'GET',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

// Async thunk to fetch users kazoos they created
export const getMyKazoos = createAsyncThunk('kazoo/getMyKazoos', async () => {
	try {
		const res = await fetch(
			`${REQUEST_URL}/${API_VERSION}/user/get-my-kazoos`,
			{
				method: 'GET',
				credentials: 'include',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
			}
		)
		const result = await res.json()
		return result
	} catch (err) {
		console.log(err.message)
	}
})

// Async thunk to fetch users kazoos they are invited to
export const getOtherKazoos = createAsyncThunk(
	'kazoo/getOtherKazoos',
	async () => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/user/get-other-kazoos`,
				{
					method: 'GET',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const getKazoos = createAsyncThunk('kazoo/getKazoos', async () => {
	try {
		const res = await fetch(
			`${REQUEST_URL}/${API_VERSION}/vendor/get-kazoos`,
			{
				method: 'GET',
				credentials: 'include',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
			}
		)
		const result = await res.json()
		return result
	} catch (err) {
		console.log(err.message)
	}
})

export const getKazoosforStaff = createAsyncThunk(
	'kazoo/getKazoosforStaff',
	async () => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/staff/get-kazoos-staff`,
				{
					method: 'GET',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const getPlannerKazoos = createAsyncThunk(
	'getPlannerKazoos',
	async () => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/vendor/get-kazoos`,
				{
					method: 'GET',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
				}
			)
			const result = await res.json()
			// Map vendor response format to planner format
			if (result?.success && result?.kazoos) {
				result.kazoos = result.kazoos.map(k => ({
					...k,
					engageCouple: `${k.brideFirstName || 'Marrier'} & ${k.groomFirstName || 'Marrier'}`,
					name: k.kazooName
				}))
			}
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

// Async thunk to create a kazoo
export const createKazoo = createAsyncThunk(
	'kazoo/createKazoo',
	async (data) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/kazoo/create-kazoo`,
				{
					method: 'POST',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ data }),
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const createKazooAsPlanner = createAsyncThunk(
	'kazoo/createKazooAsPlanner',
	async (data) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/kazoo/create-kazoo-as-planner`,
				{
					method: 'POST',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ data }),
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

// Async thunk to update a kazoo (payload: { kazooId, ...fields })
export const updateKazoo = createAsyncThunk(
	'kazoo/updateKazoo',
	async (data) => {
		const { kazooId, ...body } = data
		if (!kazooId) throw new Error('kazooId is required')
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/kazoo/update-kazoo/${kazooId}`,
				{
					method: 'PATCH',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
					body: JSON.stringify(body),
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

// Async thunk to delete a kazoo
export const deleteKazoo = createAsyncThunk(
	'kazoo/deleteKazoo',
	async (kazooId) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/kazoo/delete-kazoo/${kazooId}`,
				{
					method: 'DELETE',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ kazooId }),
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const getKazooDashboard = createAsyncThunk(
	'kazoo/getKazooDashboard',
	async (kazooId) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/kazoo/get-kazoo-dashboard/${kazooId}`,
				{
					method: 'GET',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const getKazooMembers = createAsyncThunk(
	'kazoo/getKazooMembers',
	async (kazooId) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/kazoo/get-kazoo-members/${kazooId}`,
				{
					method: 'GET',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const getKazooVenues = createAsyncThunk(
	'kazoo/getKazooVenues',
	async (kazooId) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/kazoo/get-kazoo-venues/${kazooId}`,
				{
					method: 'GET',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const fetchKazooDetails = createAsyncThunk(
	'kazoo/fetchDetails',
	async (kazooId) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/kazoo/get-kazoo-details/${kazooId}`,
				{
					method: 'GET',
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
					},
				}
			)
			const data = await res.json()
			return data
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const fetchKazooDetailsNew = createAsyncThunk(
	'kazoo/fetchDetailsnew',
	async (kazooId) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/kazoo/get-kazoo-details-new/${kazooId}`,
				{
					method: 'GET',
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
					},
				}
			)
			const data = await res.json()
			return data
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const acceptKazooInvitationUser = createAsyncThunk(
	'kazoo/acceptKazooInvitationUser',
	async ({ kazooId }) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/user/accept-kazoo-invitation/${kazooId}`,
				{
					method: 'PATCH',
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
					},
				}
			)

			const data = await res.json()
			return data
		} catch (err) {
			console.error(err.message)
		}
	}
)

export const rejectKazooInvitationUser = createAsyncThunk(
	'kazoo/rejectKazooInvitationUser',
	async ({ kazooId, userId, reason }) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/user/remove-member-from-kazoo/${kazooId}`,
				{
					method: 'PATCH',
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ userId, reason }),
				}
			)
			const data = await res.json()
			return data
		} catch (err) {
			console.error(err.message)
		}
	}
)

export const acceptKazooInvitationVendor = createAsyncThunk(
	'kazoo/acceptKazooInvitationVendor',
	async ({ kazooId, vendorId }) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/vendor/accept-kazoo-invitation/${kazooId}`,
				{
					method: 'PATCH',
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ vendorId }),
				}
			)

			const result = await res.json()
			return result
		} catch (err) {
			console.error(err.message)
		}
	}
)

export const rejectKazooInvitationVendor = createAsyncThunk(
	'kazoo/rejectKazooInvitationVendor',
	async ({ kazooId, vendorId, reason }) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/vendor/remove-vendor-from-kazoo/${kazooId}`,
				{
					method: 'PATCH',
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ vendorId, reason }),
				}
			)
			const data = await res.json()
			return data
		} catch (err) {
			console.error(err.message)
		}
	}
)

export const invitePartnerToKazoo = createAsyncThunk(
	'kazoo/invitePartnerToKazoo',
	async ({ kazooId, email }) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/kazoo/invite-partner/${kazooId}`,
				{
					method: 'PATCH',
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ email }),
				}
			)
			const data = await res.json()
			return data
		} catch (err) {
			console.error(err.message)
		}
	}
)

export const deletePartnerFromKazoo = createAsyncThunk(
	'kazoo/deletePartnerFromKazoo',
	async ({ kazooId, email }) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/kazoo/delete-partner/${kazooId}`,
				{
					method: 'PATCH',
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ email }),
				}
			)
			const data = await res.json()
			return data
		} catch (err) {
			console.error(err.message)
		}
	}
)

export const getCategoryLineItems = createAsyncThunk(
	'kazoo/getCategoryLineItems',
	async (kazooId) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/kazoo/get-kazoo-category-allocations/${kazooId}`,
				{
					method: 'GET',
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
					},
				}
			)
			const data = await res.json()
			return data
		} catch (err) {
			console.error(err.message)
		}
	}
)

export const createCategoryLineItem = createAsyncThunk(
	'kazoo/createCategoryLineItem',
	async ({ kazooId, category, lineItem }) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/kazoo/create-category-line-item/${kazooId}`,
				{
					method: 'PATCH',
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ category, lineItem }),
				}
			)
			const data = await res.json()
			data.category = category
			return data
		} catch (err) {
			console.error(err.message)
		}
	}
)

export const deleteCategoryLineItem = createAsyncThunk(
	'kazoo/deleteCategoryLineItem',
	async ({ kazooId, category, lineItemId }) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/kazoo/delete-category-line-item/${kazooId}`,
				{
					method: 'PATCH',
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ category, lineItemId }),
				}
			)
			const data = await res.json()
			data.category = category
			data.lineItemId = lineItemId
			return data
		} catch (err) {
			console.error(err.message)
		}
	}
)

export const updateCategoryLineItem = createAsyncThunk(
	'kazoo/updateCategoryLineItem',
	async ({ kazooId, category, lineItem }) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/kazoo/update-category-line-item/${kazooId}`,
				{
					method: 'PATCH',
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ category, lineItem }),
				}
			)
			const data = await res.json()
			data.category = category
			return data
		} catch (err) {
			console.error(err.message)
		}
	}
)

export const updateKazooBudget = createAsyncThunk(
	'kazoo/updatedKazooBudget',
	async ({ kazooId, newBudget }) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/kazoo/update-kazoo-budget/${kazooId}`,
				{
					method: 'PATCH',
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ newBudget }),
				}
			)
			const data = await res.json()
			return data
		} catch (err) {
			console.error(err.message)
		}
	}
)

export const getKazooDate = createAsyncThunk(
	'kazoo/getKazooDate',
	async (kazooId) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/kazoo/get-kazoo-date/${kazooId}`,
				{
					method: 'GET',
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
					},
				}
			)
			const data = await res.json()
			return data
		} catch (err) {
			console.error(err.message)
		}
	}
)

export const verifyVenueAddress = createAsyncThunk(
	'googleMapsApi/addressValidationAPI',
	async ({ input, type }) => {
		try {
			const res = await fetch(
				`https://addressvalidation.googleapis.com/v1:validateAddress?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`,
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						address: { addressLines: [input] },
					}),
				}
			)
			const data = await res.json()
			data.type = type
			data.input = input
			return data
		} catch (err) {
			console.error(err.message)
		}
	}
)

export const getCuratedImages = createAsyncThunk(
	'kazoo/getCuratedImages',
	async (kazooId) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/kazoo/get-curated-images/${kazooId}`,
				{
					method: 'GET',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const getKazooFavoriteVendors = createAsyncThunk(
	'getKazooFavoriteVendors',
	async (kazooId) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/kazoo/favorite-vendors/${kazooId}`,
				{
					method: 'GET',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const toggleFavoriteVendor = createAsyncThunk(
	'toggleFavoriteVendor',
	async ({ kazooId, vendorId }) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/kazoo/toggle-favorite-vendor/${kazooId}`,
				{
					method: 'PATCH',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ vendorId }),
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const fetchCategoryBudget = createAsyncThunk(
	'kazoo/fetchCategoryBudget',
	async ({ category, kazooId }) => {
		try {
			const params = new URLSearchParams()
			if (category) params.set('category', category)

			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/kazoo/get-category-budget/${kazooId}?${params.toString()}`,
				{
					method: 'GET',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
				}
			)

			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
			return { success: false, message: err.message }
		}
	}
)

const initialState = {
	// user account
	myKazoos: [],
	otherKazoos: [],
	// vendor / planner account
	kazoos: [],
	// below are for when an account enters a kazoo
	authorized: false,
	authorizedVendor: false,
	checkKazooAuthLoading: true,
	dashboardData: {},
	kazooMembers: { bride: {}, groom: {} },
	kazooVenues: [],
	kazooDetails: null,
	role: '',
	permissions: {},
	categoryAllocations: {},
	// ---
	paymentOverview: [0, 0, 0], // [pending, upcoming, paid]
	spendingForcast: [0, 0, 0, 0, 0, 0], // next 6 months
	mostExpensiveCategories: [],
	allocatedBudget: 0,
	allocatedPercentage: 0,
	// ---
	budget: 0,
	ceremonyVibe: [],
	ceremonyVibeImages: [],
	kazooDate: null,
	selectedImages: [],
	ceremonyVenueVerified: false,
	ceremonyVenueError: {},
	captureCount: {},
	receptionVenueVerified: false,
	receptionVenueError: {},
	favoriteVendors: [],

	categoryBudget: null,
	surveyData: {},
}

const kazooEventSlice = createSlice({
	name: 'kazooEvent',
	initialState,
	reducers: {
		resetVenueVerification: (state, { payload }) => {
			if (payload.type === 'Ceremony') {
				state.ceremonyVenueVerified = false
				state.ceremonyVenueError = {}
			} else {
				state.receptionVenueVerified = false
				state.receptionVenueError = {}
			}
		},
		calculateBudgetData: (state) => {
			let paymentOverview = [0, 0, 0] // [pending, upcoming, paid]
			let spendingForcast = [0, 0, 0, 0, 0, 0]
			let categories = []
			let allocatedBudget = 0

			const now = new Date()

			const startSixMonths = new Date(
				now.getFullYear(),
				now.getMonth(),
				1
			)
			startSixMonths.setMonth(now.getMonth() + 1)

			const endSixMonths = new Date(now.getFullYear(), now.getMonth())
			endSixMonths.setMonth(startSixMonths.getMonth() + 6)

			for (const key in state.categoryAllocations) {
				let total = 0
				for (const item of state.categoryAllocations[key]) {
					total += item.total
					const remaining = item.total - item.paid
					if (remaining === 0) {
						paymentOverview[2] += 1
					} else if (remaining === item.total) {
						paymentOverview[1] += 1
					} else {
						paymentOverview[0] += 1
					}

					let itemDate = new Date(item.dueDate)
					if (startSixMonths <= itemDate && itemDate < endSixMonths) {
						const diffTime = Math.abs(
							itemDate.getTime() - startSixMonths.getTime()
						)
						const diffDays = Math.ceil(
							diffTime / (1000 * 60 * 60 * 24)
						)
						const monthsAway = Math.floor(diffDays / 30.44) // Average days in a month
						spendingForcast[monthsAway] += remaining
					}
				}

				allocatedBudget += total
				if (total)
					categories.push({
						title: key,
						amount: `$${total.toLocaleString()} / $${state.budget.toLocaleString()}`,
						percent: Number(
							((total / state.budget) * 100).toFixed(2)
						),
					})
			}

			for (let i = 0; i < state.paymentOverview.length; i++) {
				state.paymentOverview[i] = paymentOverview[i]
			}

			for (let i = 0; i < state.spendingForcast.length; i++) {
				state.spendingForcast[i] = spendingForcast[i]
			}

			categories = categories.sort((a, b) => b.percent - a.percent)
			state.mostExpensiveCategories = categories.slice(0, 5)
			state.allocatedBudget = allocatedBudget
			state.allocatedPercentage = (
				(allocatedBudget / state.budget) *
				100
			).toFixed(2)
		},
		setSurveyData: (state, { payload }) => {
			state.surveyData = {
				...state.surveyData,
				...payload,
			}
		},
		clearSurveyData: (state) => {
			state.surveyData = {}
		},
	},

	extraReducers: (builder) => {
		builder
			.addCase(checkKazooAuth.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'You are not part of this wedding',
						'error'
					)
					window.location.assign('/user-dashboard')
					return
				}
				state.role = payload.role
				state.permissions = payload.permissions
				state.checkKazooAuthLoading = false
				state.authorized = true
				if (['vendor', 'vendor-staff', 'planner-staff'].includes(payload.role)) {
					state.authorizedVendor = true
				}
			})

			.addCase(checkKazooAuthVendor.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(payload?.message || 'Unauthorized', 'error')
					window.location.assign('/user-dashboard')
					return
				}
				state.role = payload.role
				state.permissions = payload.permissions
				state.checkKazooAuthLoading = false
				state.authorizedVendor = true
				state.authorized = true
			})

			.addCase(getKazooDashboard.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Could not fetch wedding dashboard',
						'error'
					)
					return
				}
				state.dashboardData = payload.dashboardData
			})

			.addCase(getKazooMembers.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Could not fetch wedding members',
						'error'
					)
					return
				}
				state.kazooMembers = {
					bride: payload.kazooMembers?.bride || {},
					groom: payload.kazooMembers?.groom || {}
				}
				if (payload.kazooMembers?.planners?.length > 0) {
					state.kazooMembers.planners = payload.kazooMembers.planners
				}
			})

			.addCase(fetchCategoryBudget.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Could not fetch category budget',
						'error'
					)
					state.categoryBudget = null
					return
				}

				state.categoryBudget = payload.amount
			})

			.addCase(getKazooVenues.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Could not fetch wedding venues',
						'error'
					)
					return
				}
				state.kazooVenues = payload.kazooVenues
			})

			.addCase(getMyKazoos.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					return
				}
				state.myKazoos = payload.myKazoos
			})

			.addCase(getOtherKazoos.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					return
				}
				state.otherKazoos = payload.otherKazoos
			})

			.addCase(getKazoos.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					return
				}
				state.kazoos = payload.kazoos
			})

			.addCase(getKazoosforStaff.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					return
				}
				state.kazoos = payload.kazoos
			})

			.addCase(getPlannerKazoos.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Could not fetch planner kazoos',
						'error'
					)
					return
				}
				state.kazoos = payload.kazoos
			})

			.addCase(createKazoo.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Could not create wedding',
						'error'
					)
					return
				}
				toastHelper(
					payload.message || 'Wedding created successfully',
					'success'
				)
				window.location.assign(
					`/wedding/wedding-dashboard/${payload.kazooId}`
				)
			})

			.addCase(createKazooAsPlanner.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Could not create wedding',
						'error'
					)
					return
				}
				toastHelper(
					payload.message || 'Wedding created successfully',
					'success'
				)
				window.location.assign(
					`/wedding/wedding-dashboard/${payload.kazooId}`
				)
			})

			.addCase(updateKazoo.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Could not update wedding',
						'error'
					)
					return
				}
				toastHelper(
					payload.message || 'Wedding updated successfully',
					'success'
				)
			})

			.addCase(deleteKazoo.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Could not delete wedding',
						'error'
					)
					return
				}
				state.myKazoos = state.myKazoos.filter(
					(kazoo) => kazoo.kazooId !== payload.kazooId
				)
				toastHelper(
					payload.message || 'Wedding deleted successfully',
					'success'
				)
			})

			.addCase(fetchKazooDetails.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Could not fetch wedding info',
						'error'
					)
					return
				}
				state.kazooDetails = payload
			})

			.addCase(
				acceptKazooInvitationUser.fulfilled,
				(state, { payload }) => {
					if (!payload || !payload.success) {
						toastHelper(
							payload?.message ||
							'Could not accept wedding invitation',
							'error'
						)
						return
					}
					toastHelper(
						payload.message ||
						'Successfully accepted wedding invitation',
						'success'
					)
					state.otherKazoos = state.otherKazoos.map((kazoo) => {
						if (kazoo._id === payload.kazooId) {
							return {
								...kazoo,
								status: undefined,
							}
						}
						return kazoo
					})
					state.myKazoos = state.myKazoos.map((kazoo) => {
						if (kazoo._id === payload.kazooId) {
							return {
								...kazoo,
								status: undefined,
							}
						}
						return kazoo
					})
				}
			)

			.addCase(
				acceptKazooInvitationVendor.fulfilled,
				(state, { payload }) => {
					if (!payload || !payload.success) {
						toastHelper(
							payload?.message ||
							'Could not accept wedding invitation',
							'error'
						)
						return
					}
					toastHelper(
						payload.message ||
						'Successfully accepted wedding invitation',
						'success'
					)
					state.kazoos = state.kazoos.map((kazoo) => {
						if (kazoo._id === payload.kazooId) {
							return {
								...kazoo,
								status: undefined,
							}
						}
						return kazoo
					})
				}
			)

			.addCase(
				rejectKazooInvitationUser.fulfilled,
				(state, { payload }) => {
					if (!payload || !payload.success) {
						toastHelper(
							'Could not reject invitation or leave wedding',
							'error'
						)
						return
					}
					toastHelper(
						'Successfully rejected invitation or left wedding',
						'success'
					)
					state.otherKazoos = state.otherKazoos.filter(
						(kazoo) => kazoo._id !== payload.kazooId
					)
					state.myKazoos = state.myKazoos.filter(
						(kazoo) => kazoo._id !== payload.kazooId
					)
				}
			)

			.addCase(
				rejectKazooInvitationVendor.fulfilled,
				(state, { payload }) => {
					if (!payload || !payload.success) {
						toastHelper(
							'Could not reject invitation or leave wedding',
							'error'
						)
						return
					}
					toastHelper(
						'Successfully rejected invitation or left wedding',
						'success'
					)

					state.kazoos = state.kazoos.filter(
						(kazoo) => kazoo._id !== payload.kazooId
					)
				}
			)

			.addCase(invitePartnerToKazoo.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Could not invite partner',
						'error'
					)
					return
				}
				toastHelper(payload.message || 'Invitation sent', 'success')
				
				// Only update state if this is a NEW partner (not a resend)
				// Check if bride slot is empty
				if (!state.kazooMembers.bride?._id && !state.kazooMembers.bride?.email) {
					state.kazooMembers.bride = payload.partner
				} 
				// Check if groom slot is empty
				else if (!state.kazooMembers.groom?._id && !state.kazooMembers.groom?.email) {
					state.kazooMembers.groom = payload.partner
				}
				// If both slots are filled, this is a resend - don't update state
			})
			
			.addCase(deletePartnerFromKazoo.fulfilled, (state, { payload }) => {
				console.log({payload});
				
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Could not remove partner',
						'error'
					)
					return
				}

				toastHelper(payload.message || 'Partner removed', 'success')

				const removedRole = payload?.removed // 'bride' | 'groom'
				state.kazooMembers = {
					...state.kazooMembers,
					[removedRole]: {},
				}
			})

			.addCase(getCategoryLineItems.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message ||
						'Could not fetch category allocations',
						'error'
					)
					return
				}

				state.categoryAllocations = payload.categoryAllocations
				state.budget = payload.budget
				kazooEventSlice.caseReducers.calculateBudgetData(state)
			})

			.addCase(createCategoryLineItem.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Failed to create line item',
						'error'
					)
					return
				}

				toastHelper(
					payload.message || 'Successfully created line item',
					'success'
				)

				if (state.categoryAllocations[payload.category]) {
					state.categoryAllocations[payload.category].push(
						payload.lineItem
					)
				} else {
					state.categoryAllocations[payload.category] = [
						payload.lineItem,
					]
				}

				kazooEventSlice.caseReducers.calculateBudgetData(state)
			})

			.addCase(deleteCategoryLineItem.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Failed to delete line item',
						'error'
					)
					return
				}

				toastHelper(
					payload.message || 'Successfully deleted line item',
					'success'
				)

				state.categoryAllocations[payload.category] =
					state.categoryAllocations[payload.category].filter(
						(item) => item._id !== payload.lineItemId
					)
				kazooEventSlice.caseReducers.calculateBudgetData(state)
			})

			.addCase(updateCategoryLineItem.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Failed to updated line item',
						'error'
					)
					return
				}

				toastHelper(
					payload.message || 'Successfully updated line item',
					'success'
				)

				state.categoryAllocations[payload.category] =
					state.categoryAllocations[payload.category].map((item) =>
						item._id === payload.lineItem._id
							? payload.lineItem
							: item
					)
				kazooEventSlice.caseReducers.calculateBudgetData(state)
			})

			.addCase(updateKazooBudget.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Failed to update budget',
						'error'
					)
					return
				}

				toastHelper(
					payload.message || 'Successfully updated budget',
					'success'
				)

				state.budget = payload.budget
				state.allocatedPercentage = (
					(state.allocatedBudget / payload.budget) *
					100
				).toFixed(2)
			})

			.addCase(getKazooDate.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Could not fetch wedding date',
						'error'
					)
					return
				}

				state.kazooDate = payload.kazooDate
			})

			.addCase(getCuratedImages.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Could not fetch curated images',
						'error'
					)
					return
				}
				state.selectedImages = payload.selectedImages || []
			})

			.addCase(verifyVenueAddress.fulfilled, (state, { payload }) => {
				const type = payload.type
				const invalid = {
					text: 'Invalid Address',
					color: 'red',
				}

				if (!payload?.result?.verdict?.addressComplete) {
					if (type === 'Ceremony') {
						state.ceremonyVenueError = invalid
						state.ceremonyVenueVerified = false
					} else {
						state.receptionVenueError = invalid
						state.receptionVenueVerified = false
					}
					return
				}
				let inferred = false
				for (const component of payload?.result?.address
					?.addressComponents) {
					if (component.confirmationLevel !== 'CONFIRMED') {
						if (type === 'Ceremony') {
							state.ceremonyVenueError = invalid
							state.ceremonyVenueVerified = false
						} else {
							state.receptionVenueError = invalid
							state.receptionVenueVerified = false
						}
						return
					}
					inferred ||= component.inferred
				}

				if (
					inferred &&
					payload?.result?.address?.formattedAddress !== payload.input
				) {
					const inf = {
						text: `Did you mean: ${payload?.result?.address?.formattedAddress}?`,
						color: 'orange',
						address: payload?.result?.address?.formattedAddress,
					}
					if (type === 'Ceremony') {
						state.ceremonyVenueError = inf
						state.ceremonyVenueVerified = false
					} else {
						state.receptionVenueError = inf
						state.receptionVenueVerified = false
					}
					return
				}

				const valid = {
					text: 'Address Validated!',
					color: 'green',
				}

				if (type === 'Ceremony') {
					state.ceremonyVenueError = valid
					state.ceremonyVenueVerified = true
				} else {
					state.receptionVenueError = valid
					state.receptionVenueVerified = true
				}
			})
			.addCase(
				getKazooFavoriteVendors.fulfilled,
				(state, { payload }) => {
					if (!payload || !payload.success) {
						toastHelper(
							payload?.message ||
							'Failed to fetch favorite vendors',
							'error'
						)
						return
					}
					state.favoriteVendors = payload.favoriteVendors
				}
			)
			.addCase(toggleFavoriteVendor.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Failed to toggle favorite vendor',
						'error'
					)
					return
				}
				toastHelper(
					payload?.message || 'Successfully toggled favorite vendor',
					'success'
				)
			})

			.addCase(fetchKazooDetailsNew.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Could not fetch wedding details',
						'error'
					)
					return
				}

				const k = payload.kazoo || {}

				state.kazooDetails = k
				state.budget = k.budget ?? 0
				state.kazooDate = k.startDate ?? null
				state.selectedImages = Array.isArray(k.selectedImages)
					? k.selectedImages
					: []
				state.captureCount = k.captureCount || {}
				state.categoryAllocations =
					k.categoryAllocations &&
						typeof k.categoryAllocations === 'object'
						? k.categoryAllocations
						: {}
			})
	},
})

export const { resetVenueVerification, setSurveyData, clearSurveyData } =
	kazooEventSlice.actions
export default kazooEventSlice.reducer
